import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';
import pl from './pl.json';

/**
 * Bilingual scaffold (English default, Polish available).
 *
 * The auth flow calls `i18n.changeLanguage(user.preferred_language)`
 * on login and on profile updates. We also keep the last-known choice
 * in `localStorage` so the login page renders in the user's chosen
 * language after they log out and back in — the actual authoritative
 * value stays on the User record server-side.
 */
export const SUPPORTED_LANGUAGES = ['en', 'pl'];
export const LANGUAGE_LABELS = { en: 'English', pl: 'Polski' };

const STORAGE_KEY = 'expertfix_ui_language';

const bootstrapLang = (() => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) return saved;
  } catch { /* ignore */ }
  return 'en';
})();

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      pl: { translation: pl },
    },
    lng: bootstrapLang,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

// Persist changes so subsequent page loads bootstrap correctly.
i18n.on('languageChanged', (lng) => {
  try { localStorage.setItem(STORAGE_KEY, lng); } catch { /* ignore */ }
});

/**
 * Merge runtime overrides fetched from ``/api/i18n/overrides`` into the
 * live i18next runtime. ``payload`` is ``{en: {key: value}, pl: {...}}``
 * where keys use dot-notation (e.g. ``"invoices.title"``).
 *
 * Uses ``addResource`` per entry so the deep tree is patched in place —
 * the existing static default remains for every key that has no override.
 */
const OVERRIDES_CACHE = 'i18n_overrides_cache';

export const applyOverrides = (payload, cache = true) => {
  if (!payload || typeof payload !== 'object') return;
  if (cache) { try { localStorage.setItem(OVERRIDES_CACHE, JSON.stringify(payload)); } catch { /* ignore */ } }
  SUPPORTED_LANGUAGES.forEach((lng) => {
    const bundle = payload[lng] || {};
    Object.entries(bundle).forEach(([key, value]) => {
      if (typeof value === 'string') {
        i18n.addResource(lng, 'translation', key, value);
      }
    });
  });
};

// Apply the last known overrides synchronously so labels computed at import time already use them.
try { applyOverrides(JSON.parse(localStorage.getItem(OVERRIDES_CACHE) || 'null'), false); } catch { /* ignore */ }

// Overrides are UI strings, safe to load before sign-in so public pages show edited texts too.
export const loadPublicOverrides = async () => {
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/i18n/public-overrides`);
    if (!res.ok) return;
    applyOverrides(await res.json());
    i18n.changeLanguage(i18n.language);
  } catch { /* keep static defaults */ }
};
loadPublicOverrides();

export default i18n;
